import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, kg } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CatchCertVerification, CatchReport, ExportCertificate, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const FAO_SPECIES = [
  { code: 'YFT', name: 'Yellowfin tuna' },
  { code: 'SKJ', name: 'Skipjack tuna' },
  { code: 'BET', name: 'Bigeye tuna' },
  { code: 'ALB', name: 'Albacore tuna' },
  { code: 'SWO', name: 'Swordfish' },
  { code: 'BLM', name: 'Black marlin' },
  { code: 'DOL', name: 'Common dolphinfish (mahi-mahi)' },
  { code: 'SNA', name: 'Snappers nei (job fish)' },
  { code: 'GPX', name: 'Groupers nei' },
  { code: 'LOB', name: 'Spiny lobsters nei' },
  { code: 'OCT', name: 'Octopus' },
] as const;
const PRODUCT_FORMS = ['WHOLE_FRESH', 'WHOLE_FROZEN', 'FILLET_FRESH', 'FILLET_FROZEN', 'CANNED', 'CHILLED_LOINS'] as const;
const LIST_ROLES = ['EXPORT_CERT_OFFICER', 'SFA_LICENSING'];

/** M32 Fisheries — EU IUU export (catch) certificate chain: apply, review, issue, and trace back to the exact catch-report lines a consignment was sourced from. */
export function ExportCertificates() {
  const { persona } = useAuth();
  const isExportOfficer = persona ? persona.roles.includes('EXPORT_CERT_OFFICER') : false;
  const canList = persona ? persona.roles.some((r) => LIST_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m32/export-certificates?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<ExportCertificate>>(path);

  return (
    <div>
      <PageHeader
        title="Export Certificates"
        subtitle="EU IUU catch-certificate chain — sourced against verified catch-report lines (M32)."
        actions={
          <>
            <button className="btn-secondary" onClick={() => setShowVerify(true)}>
              Verify certificate chain
            </button>
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New application
            </button>
          </>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Cert no, exporter, destination…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'UNDER_REVIEW', 'FEE_PENDING', 'ISSUED', 'REJECTED', 'REVOKED'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>

      {!canList ? <RoleNotice roles={LIST_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No export certificates match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Cert No</th>
                <th className="table-head">Species / form</th>
                <th className="table-head">Net weight</th>
                <th className="table-head">Destination</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{c.certNo}</td>
                  <td className="table-cell text-xs">
                    {c.speciesCode} · {c.productForm.replaceAll('_', ' ')}
                  </td>
                  <td className="table-cell text-xs">{kg(c.netWeightKg)}</td>
                  <td className="table-cell text-xs text-slate-500">{c.destinationCountry}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <CertDetailModal
          id={selectedId}
          isExportOfficer={isExportOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <NewCertModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {showVerify ? <VerifyChainModal onClose={() => setShowVerify(false)} /> : null}
    </div>
  );
}

function CertDetailModal({ id, isExportOfficer, onClose, onChanged }: { id: string; isExportOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const cq = useApiQuery<ExportCertificate>(`/api/v1/m32/export-certificates/${id}`);
  const c = cq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecision, setShowDecision] = useState<'approve' | 'reject' | 'revoke' | null>(null);
  const [note, setNote] = useState('');
  const [chain, setChain] = useState<CatchCertVerification | null>(null);
  const [chainBusy, setChainBusy] = useState(false);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const confirm = () =>
    call(() => {
      if (showDecision === 'approve') return api.post(`/api/v1/m32/export-certificates/${id}/review`, { approve: true, note: note || undefined });
      if (showDecision === 'reject') return api.post(`/api/v1/m32/export-certificates/${id}/review`, { approve: false, rejectionReason: note });
      return api.post(`/api/v1/m32/export-certificates/${id}/revoke`, { reason: note });
    });

  const loadChain = async () => {
    if (!c) return;
    setChainBusy(true);
    try {
      setChain(await api.get<CatchCertVerification>(`/api/v1/m32/verify/catch-cert/${c.certNo}`));
    } catch {
      setChain(null);
    } finally {
      setChainBusy(false);
    }
  };

  return (
    <Modal title={c ? c.certNo : 'Export certificate'} onClose={onClose} wide>
      {cq.error ? <ErrorNote message={cq.error} /> : null}
      {cq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={c.status} />} />
            <Detail label="Exporter" value={`${c.exporterType}:${c.exporterId}`} mono />
            <Detail label="Species" value={c.speciesCode} />
            <Detail label="Product form" value={c.productForm.replaceAll('_', ' ')} />
            <Detail label="Net weight" value={kg(c.netWeightKg)} />
            <Detail label="Destination" value={c.destinationCountry} />
            <Detail label="Issued" value={formatDateTime(c.issuedAt)} />
            <Detail label="DocVault ref" value={c.docVaultRef ?? '—'} mono />
            <Detail label="Customs link (M09)" value={c.customsLinkStatus ?? 'PENDING_INTEGRATION'} />
          </div>
          {c.rejectionReason ? <ErrorNote message={`Rejected: ${c.rejectionReason}`} /> : null}
          {c.revokedReason ? <ErrorNote message={`Revoked: ${c.revokedReason}`} /> : null}

          <div>
            <div className="mb-1 flex items-center justify-between">
              <div className="field-label !mb-0">Sourcing chain ({c.sources?.length ?? 0} line(s))</div>
              <button className="text-xs font-semibold text-brand-700 underline" disabled={chainBusy} onClick={loadChain}>
                {chainBusy ? 'Loading…' : 'Load full traceability chain'}
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="table-head">Catch report line</th>
                  <th className="table-head">Allocated kg</th>
                </tr>
              </thead>
              <tbody>
                {(c.sources ?? []).map((s) => (
                  <tr key={s.id}>
                    <td className="table-cell font-mono text-xs">{s.catchReportLineId}</td>
                    <td className="table-cell text-xs">{kg(s.allocatedKg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {chain ? (
              chain.found && chain.chain ? (
                <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="table-head">Report ref</th>
                        <th className="table-head">Vessel</th>
                        <th className="table-head">Zone</th>
                        <th className="table-head">Trip date</th>
                        <th className="table-head">Method</th>
                        <th className="table-head">kg</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chain.chain.map((e, i) => (
                        <tr key={i}>
                          <td className="table-cell font-mono">{e.catchReportRef}</td>
                          <td className="table-cell font-mono">{e.vesselRegNo}</td>
                          <td className="table-cell">{e.zoneCode}</td>
                          <td className="table-cell">{formatDate(e.tripDate)}</td>
                          <td className="table-cell">{e.method.replaceAll('_', ' ')}</td>
                          <td className="table-cell">{kg(e.allocatedKg)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-400">Not found via the public verify endpoint.</p>
              )
            ) : null}
          </div>

          {isExportOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showDecision ? (
                <div className="flex flex-wrap gap-2">
                  {c.status === 'UNDER_REVIEW' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowDecision('approve')}>
                        Approve (open fee intent)
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('reject')}>
                        Reject
                      </button>
                    </>
                  ) : null}
                  {c.status === 'ISSUED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label={showDecision === 'approve' ? 'Note (optional)' : 'Reason'}>
                    <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowDecision(null)}>
                      Cancel
                    </button>
                    <button className={showDecision === 'approve' ? 'btn-success' : 'btn-danger'} disabled={busy} onClick={confirm}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function VerifyChainModal({ onClose }: { onClose: () => void }) {
  const [certNo, setCertNo] = useState('');
  const [result, setResult] = useState<CatchCertVerification | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verify = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.get<CatchCertVerification>(`/api/v1/m32/verify/catch-cert/${encodeURIComponent(certNo)}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Verify catch-certificate chain" onClose={onClose} wide>
      <div className="flex gap-2">
        <input className="field-input" placeholder="Certificate number, e.g. SFA-2026-000123" value={certNo} onChange={(e) => setCertNo(e.target.value)} />
        <button className="btn-primary" disabled={busy || !certNo} onClick={verify}>
          {busy ? 'Checking…' : 'Verify'}
        </button>
      </div>
      {error ? (
        <div className="mt-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {result ? (
        !result.found ? (
          <p className="mt-3 text-sm text-slate-500">No certificate found for that number.</p>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Detail label="Valid" value={<StatusBadge status={result.valid ? 'ISSUED' : 'REVOKED'} />} />
              <Detail label="Species / form" value={`${result.speciesCode} · ${result.productForm?.replaceAll('_', ' ')}`} />
              <Detail label="Net weight" value={kg(result.netWeightKg)} />
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="table-head">Report ref</th>
                  <th className="table-head">Vessel</th>
                  <th className="table-head">Zone</th>
                  <th className="table-head">Trip date</th>
                  <th className="table-head">Method</th>
                  <th className="table-head">kg</th>
                </tr>
              </thead>
              <tbody>
                {(result.chain ?? []).map((e, i) => (
                  <tr key={i}>
                    <td className="table-cell font-mono">{e.catchReportRef}</td>
                    <td className="table-cell font-mono">{e.vesselRegNo}</td>
                    <td className="table-cell">{e.zoneCode}</td>
                    <td className="table-cell">{formatDate(e.tripDate)}</td>
                    <td className="table-cell">{e.method.replaceAll('_', ' ')}</td>
                    <td className="table-cell">{kg(e.allocatedKg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </Modal>
  );
}

function ReportLinePicker({ selected, onToggle }: { selected: string[]; onToggle: (lineId: string) => void }) {
  const [q, setQ] = useState('');
  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '15', status: 'VERIFIED' });
    if (q) params.set('q', q);
    return `/api/v1/m32/catch-reports?${params.toString()}`;
  }, [q]);
  const reportsQ = useApiQuery<Page<CatchReport>>(path);

  return (
    <div>
      <input className="field-input mb-2" placeholder="Search VERIFIED catch reports by ref/vessel…" value={q} onChange={(e) => setQ(e.target.value)} />
      {reportsQ.loading ? (
        <Spinner />
      ) : (reportsQ.data?.items.length ?? 0) === 0 ? (
        <p className="text-xs text-slate-400">No VERIFIED catch reports found.</p>
      ) : (
        <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-2">
          {reportsQ.data!.items.map((r) => (
            <div key={r.id}>
              <div className="text-xs font-semibold text-slate-500">
                {r.reportRef} — {r.vesselRegNo} — {formatDate(r.tripDate)}
              </div>
              {r.lines.map((l) => (
                <label key={l.id} className="ml-2 flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={selected.includes(l.id)} onChange={() => onToggle(l.id)} />
                  {l.speciesCode} — {kg(l.estimatedKg)}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="mt-1 text-xs text-slate-500">{selected.length} line(s) selected</div>
    </div>
  );
}

function NewCertModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [exporterType, setExporterType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [exporterId, setExporterId] = useState('');
  const [speciesCode, setSpeciesCode] = useState<(typeof FAO_SPECIES)[number]['code']>('YFT');
  const [productForm, setProductForm] = useState<(typeof PRODUCT_FORMS)[number]>('WHOLE_FROZEN');
  const [netWeightKg, setNetWeightKg] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [consignmentDescription, setConsignmentDescription] = useState('');
  const [lineIds, setLineIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => setLineIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m32/export-certificates', {
        exporterType,
        exporterId,
        speciesCode,
        productForm,
        netWeightKg: Number.parseFloat(netWeightKg),
        destinationCountry,
        consignmentDescription: consignmentDescription || undefined,
        sourceCatchReportLineIds: lineIds,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New export certificate application" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Exporter type">
            <select className="field-input" value={exporterType} onChange={(e) => setExporterType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="ENTITY">Entity (exporting company)</option>
              <option value="PERSON">Person</option>
            </select>
          </Field>
          <Field label={exporterType === 'ENTITY' ? 'Exporter business ID' : 'Exporter NIN'}>
            <input className="field-input" value={exporterId} onChange={(e) => setExporterId(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Species">
            <select className="field-input" value={speciesCode} onChange={(e) => setSpeciesCode(e.target.value as (typeof FAO_SPECIES)[number]['code'])}>
              {FAO_SPECIES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Product form">
            <select className="field-input" value={productForm} onChange={(e) => setProductForm(e.target.value as (typeof PRODUCT_FORMS)[number])}>
              {PRODUCT_FORMS.map((p) => (
                <option key={p} value={p}>
                  {p.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Net weight (kg)">
            <input className="field-input" type="number" step="0.1" value={netWeightKg} onChange={(e) => setNetWeightKg(e.target.value)} />
          </Field>
        </div>
        <Field label="Destination country">
          <input className="field-input" value={destinationCountry} onChange={(e) => setDestinationCountry(e.target.value)} placeholder="e.g. France" />
        </Field>
        <Field label="Consignment description (optional)">
          <textarea className="field-input" rows={2} value={consignmentDescription} onChange={(e) => setConsignmentDescription(e.target.value)} />
        </Field>
        <Field label="Source catch-report lines (chain-validated at submit)">
          <ReportLinePicker selected={lineIds} onToggle={toggle} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !exporterId || !netWeightKg || !destinationCountry || lineIds.length === 0} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </Modal>
  );
}
